import Fieldset from 'components/_UI/Fieldset';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import React, { useEffect, useState } from 'react';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import Select from 'react-select';
import styled from 'styled-components';
import Button from 'components/_UI/Button';
import { useForm } from 'react-hook-form';
import fieldValidations from 'helpers/fieldValidations';
import colors from 'theme/colors';
import { FullyCentered } from 'styled/alignment/Center';
import Loader from 'components/_UI/Loader';
import buttonText from 'texts/buttons/buttonText';
import { Eye, Plus, Trash2, Upload } from 'lucide-react';
import Switch from 'components/_UI/Switch';
import fonts from 'theme/fonts';
import { useEvent } from 'context/events/Event';
import customFieldsApi from 'api/requests/customFields';
import customFieldsTypes from 'theme/customFieldsTypes';
import breakpoints from 'theme/breakpoints';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

// Placeholder mostrado no preview pra cada tipo — a maioria usa o `example`
// já definido em customFieldsTypes.js, esses aqui só não fazem sentido como
// exemplo literal digitado (data/hora mostram o formato esperado, não uma
// data específica).
const previewPlaceholderOverrides = {
    date: 'dd/mm/aaaa',
    birth_date: 'dd/mm/aaaa',
    time: '--:--',
};

function formatFieldTypeOption(option) {
    const Icon = option.icon;

    return (
        <OptionLabelRow>
            {Icon && <Icon size={15} color={colors.night} />}
            <span>{option.label}</span>
        </OptionLabelRow>
    );
}

const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderRadius: '10px',
        borderColor: state.isFocused ? panelAccent(colors.secondaryPurple) : '#ddd',
        boxShadow: state.isFocused ? `0 0 0 2px ${panelAccentAlpha(colors.secondaryPurple, '22')}` : 'none',
        fontSize: fonts.sizes.default,
        minHeight: '42px',
        '&:hover': { borderColor: panelAccent(colors.secondaryPurple) },
    }),
    option: (base, state) => ({
        ...base,
        fontSize: fonts.sizes.default,
        backgroundColor: state.isSelected ? panelAccent(colors.secondaryPurple) : state.isFocused ? `${panelAccentAlpha(colors.secondaryPurple, '0C')}` : 'white',
        color: state.isSelected ? 'white' : colors.nightDark,
        '&:active': { backgroundColor: `${panelAccentAlpha(colors.secondaryPurple, '1A')}` },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    }),
};

// Espelha, sem interatividade, o campo exatamente como o comprador vai ver na
// hora de finalizar a compra — assim quem está montando o formulário não
// precisa imaginar ou testar o fluxo de compra pra saber o resultado.
function FieldPreviewInput({ field }) {
    const type = field.type?.value;
    const typeInfo = field.type;

    if (!type || !typeInfo) return null;

    if (type === 'radio' || type === 'checkbox') {
        const options = field.options?.length ? field.options : [{ label: 'Opção 1' }, { label: 'Opção 2' }];

        return (
            <MockOptionsList>
                {options.map((option, index) => (
                    <MockOptionRow key={option.id || index}>
                        <MockOptionControl round={type === 'radio'} />
                        {option.label?.trim() || `Opção ${index + 1}`}
                    </MockOptionRow>
                ))}
            </MockOptionsList>
        );
    }

    if (type === 'textarea') {
        return <MockTextarea disabled readOnly placeholder={typeInfo.example} />;
    }

    if (type === 'upload') {
        return (
            <MockUpload>
                <Upload size={14} color={colors.night} />
                Escolher arquivo
                <MockUploadHint>{typeInfo.example}</MockUploadHint>
            </MockUpload>
        );
    }

    const placeholder = previewPlaceholderOverrides[type] ?? typeInfo.example;

    return <MockInput disabled readOnly placeholder={placeholder} />;
}

function FieldPreview({ field }) {
    const type = field.type?.value;
    const label = field.label?.trim();

    return (
        <PreviewPane>
            <PreviewEyebrow>
                <Eye size={13} />
                Como o comprador vai ver
            </PreviewEyebrow>

            {!type ? (
                <PreviewEmpty>
                    Escolha um tipo de campo ao lado para ver o preview aqui.
                </PreviewEmpty>
            ) : (
                <PreviewMock>
                    <PreviewMockLabel>
                        {label || 'Nome do campo'}
                        {field.required && <RequiredMark>*</RequiredMark>}
                    </PreviewMockLabel>
                    <FieldPreviewInput field={field} />
                </PreviewMock>
            )}
        </PreviewPane>
    );
}

export default function CustomFieldsForm({ visible, isEdit, toast, onClose, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState([{ id: Date.now(), type: null, label: '', required: false, options: [] }]);

    const {event} = useEvent();

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors }
    } = useForm({defaultValues: {}});

    useEffect(() => {
        if (isEdit) {
            const updatedFields = isEdit.map(field => ({
                ...field,
                type: customFieldsTypes.find(type => type.value === field.type),
                options: field.options
                    ? field.options.map(option => ({ id: Date.now() + Math.random(), label: option }))
                    : []
            }));

            const defaultValues = updatedFields.reduce((acc, field) => {
                acc[`field_${field.id}_label`] = field.label;
                field.options.forEach((option, index) => {
                    acc[`field_${field.id}_option_${index}_label`] = option.label;
                });
                return acc;
            }, {});

            reset(defaultValues);
            setFields(updatedFields);
        }
    }, [isEdit, reset]);


    const addField = () => {
        setFields([...fields, { id: Date.now(), type: null, label: '', required: false, options: [] }]);
    };

    const removeField = (id) => {
        if (fields.length > 1) {
            const updatedFields = fields.filter((field) => field.id !== id);
            setFields(updatedFields);
        }
    };

    const toggleRequired = (id) => {
        const updatedFields = fields.map((field) =>
            field.id === id ? { ...field, required: !field.required } : field
        );
        setFields(updatedFields);
    };

    const addOption = (fieldId) => {
        const updatedFields = fields.map((field) =>
            field.id === fieldId
                ? { ...field, options: [...field.options, { id: Date.now(), label: '' }] }
                : field
        );
        setFields(updatedFields);
    };

    const removeOption = (fieldId, optionId) => {
        const updatedFields = fields.map((field) =>
            field.id === fieldId
                ? { ...field, options: field.options.filter(option => option.id !== optionId) }
                : field
        );
        setFields(updatedFields);
    };

    const updateOptionLabel = (fieldId, optionId, label) => {
        const updatedFields = fields.map((field) =>
            field.id === fieldId
                ? {
                    ...field,
                    options: field.options.map(option =>
                        option.id === optionId ? { ...option, label } : option
                    )
                }
                : field
        );
        setFields(updatedFields);
    };

    async function onSubmitForm() {
        const content = fields.map(field => ({
            label: watch(`field_${field.id}_label`),
            type: field.type?.value,
            required: field.required,
            options: field.options.map(option => option.label)
        }));

        const payload = {
            event_id: event?.id,
            custom_fields: content
        }

        setLoading(true);

        try {
            await customFieldsApi.create(payload)
            onSuccess();
            toast.success('Formulário criado com sucesso!', {
                position: 'top-center'
            })
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }

    async function onEditSubmit() {
        const content = fields.map(field => ({
            label: watch(`field_${field.id}_label`),
            type: field.type?.value,
            required: field.required,
            options: field.options.map(option => option.label)
        }));

        const payload = {
            event_id: event?.id,
            custom_fields: content
        }

        setLoading(true);

        try {
            await customFieldsApi.update(payload)
            onSuccess();
            toast.success('Formulário atualizado com sucesso!', {
                position: 'top-center'
            })
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal isOpen={visible} onClose={onClose} childrenOverflowY='scroll' size='xLarge'>
            <ModalHeader>
                <ModalTitle>{isEdit ? 'Editar campos' : 'Criar campos personalizados'}</ModalTitle>
                <ModalSubtitle>
                    {isEdit
                        ? 'Edite os campos que aparecem no formulário de inscrição.'
                        : 'Adicione campos extras para coletar informações dos participantes.'
                    }
                </ModalSubtitle>
            </ModalHeader>

            <form style={{ width: 'auto' }} onSubmit={handleSubmit(!isEdit ? onSubmitForm : onEditSubmit)}>
                <FieldsList>
                    {fields.map((field, index) => (
                        <FieldCard key={field.id}>
                            <FieldCardHeader>
                                <FieldNumber>{index + 1}</FieldNumber>
                                <FieldCardTitle>Campo {index + 1}</FieldCardTitle>
                                <RemoveFieldBtn
                                    onClick={() => removeField(field.id)}
                                    disabled={fields.length === 1}
                                    type="button"
                                >
                                    <Trash2 size={15} color={fields.length === 1 ? colors.nightLight : colors.danger} />
                                </RemoveFieldBtn>
                            </FieldCardHeader>

                            <FieldCardBody>
                                <FieldConfigColumn>
                                    <FieldRow>
                                        <FieldColumn>
                                            <InputLabel>Tipo de campo</InputLabel>
                                            <Select
                                                placeholder='Selecione o tipo...'
                                                maxMenuHeight={280}
                                                value={field.type}
                                                options={customFieldsTypes}
                                                styles={selectStyles}
                                                formatOptionLabel={formatFieldTypeOption}
                                                onChange={(selectedType) => {
                                                    const updatedFields = fields.map((f) => {
                                                        if (f.id === field.id) {
                                                            const isMultipleChoice = selectedType.value === 'radio' || selectedType.value === 'checkbox';
                                                            return {
                                                                ...f,
                                                                type: selectedType,
                                                                options: isMultipleChoice
                                                                    ? [{ id: Date.now(), label: '' }, { id: Date.now() + 1, label: '' }]
                                                                    : []
                                                            };
                                                        }
                                                        return f;
                                                    });
                                                    setFields(updatedFields);
                                                }}
                                            />
                                            {field.type && (
                                                <TypeHelpText>
                                                    {field.type.icon && <field.type.icon size={13} color={colors.night} />}
                                                    {field.type.description}
                                                </TypeHelpText>
                                            )}
                                        </FieldColumn>

                                        <FieldColumn>
                                            <Fieldset
                                                label='Nome do campo'
                                                layout='squired'
                                                placeholder='Ex: CPF, Empresa, Tamanho da camiseta...'
                                                register={register}
                                                name={`field_${field.id}_label`}
                                                validations={fieldValidations.name('Nome do campo', 2, 3000)}
                                            />
                                            {errors[`field_${field.id}_label`]?.message && (
                                                <Column>
                                                    <Text color={colors.danger} name='small'>
                                                        {errors[`field_${field.id}_label`]?.message}
                                                    </Text>
                                                </Column>
                                            )}
                                        </FieldColumn>
                                    </FieldRow>

                                    {(field.type?.value === 'radio' || field.type?.value === 'checkbox') && (
                                        <OptionsArea>
                                            <InputLabel>Opções de escolha</InputLabel>
                                            {field.options.map((option, optIndex) => (
                                                <OptionRow key={option.id}>
                                                    <OptionNumber>{optIndex + 1}.</OptionNumber>
                                                    <Fieldset
                                                        placeholder={`Opção ${optIndex + 1}`}
                                                        value={option.label}
                                                        onChangeValue={(e) => updateOptionLabel(field.id, option.id, e)}
                                                    />
                                                    <RemoveOptionBtn onClick={() => removeOption(field.id, option.id)} type="button">
                                                        <Trash2 size={14} color={colors.danger} />
                                                    </RemoveOptionBtn>
                                                </OptionRow>
                                            ))}
                                            <AddOptionBtn onClick={(e) => { e.preventDefault(); addOption(field.id); }} type="button">
                                                <Plus size={14} />
                                                Nova opção
                                            </AddOptionBtn>
                                        </OptionsArea>
                                    )}

                                    <SwitchRow>
                                        <Switch
                                            label='Campo obrigatório'
                                            labelSize='small'
                                            checked={field.required}
                                            onChange={() => toggleRequired(field.id)}
                                        />
                                    </SwitchRow>
                                </FieldConfigColumn>

                                <FieldPreview field={{ ...field, label: watch(`field_${field.id}_label`) }} />
                            </FieldCardBody>
                        </FieldCard>
                    ))}
                </FieldsList>

                <AddFieldArea>
                    <AddFieldBtn
                        type="button"
                        onClick={addField}
                        disabled={!fields.length || !(fields[fields.length - 1].type && watch(`field_${fields[fields.length - 1].id}_label`))}
                    >
                        <Plus size={18} />
                        Adicionar novo campo
                    </AddFieldBtn>
                </AddFieldArea>

                {loading ? (
                    <FullyCentered height='40px'>
                        <Loader />
                    </FullyCentered>
                ) : (
                    <FooterButtons>
                        <Button variant={'light'} category={'roundedBorded'} onClick={onClose}>
                            {buttonText.cancel}
                        </Button>
                        <Button category={'roundedBorded'} type={'submit'}>
                            {!isEdit ? buttonText.create : buttonText.update} formulário
                        </Button>
                    </FooterButtons>
                )}
            </form>
        </Modal>
    );
};

const ModalHeader = styled.div`
    margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
    font-size: ${fonts.sizes.subtitle};
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 6px 0;
`;

const ModalSubtitle = styled.p`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    margin: 0;
    line-height: 1.5;
`;

const FieldsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const FieldCard = styled.div`
    background: ${colors.neutralDark};
    border-radius: 12px;
    padding: 18px;
    border: 1px solid ${colors.night}11;
`;

const FieldCardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
`;

const FieldNumber = styled.span`
    width: 26px;
    height: 26px;
    min-width: 26px;
    border-radius: 50%;
    background: ${panelAccent(colors.secondaryPurple)};
    color: ${colors.neutralLight};
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const FieldCardTitle = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.nightDark};
    flex: 1;
`;

const RemoveFieldBtn = styled.button`
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 8px;
    border: 1px solid transparent;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
    padding: 0;

    &:hover:not(:disabled) {
        background: ${colors.danger}0A;
        border-color: ${colors.danger}44;
    }

    &:disabled {
        cursor: default;
        opacity: 0.4;
    }
`;

const FieldCardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: flex-start;

        > *:first-child { flex: 1.3; min-width: 0; }
        > *:last-child { flex: 1; min-width: 0; }
    }
`;

const FieldConfigColumn = styled.div`
    display: flex;
    flex-direction: column;
`;

const OptionLabelRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    svg { flex-shrink: 0; }
`;

const TypeHelpText = styled.p`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 8px 0 0;
    font-size: ${fonts.sizes.mini};
    line-height: 1.45;
    color: ${colors.night};

    svg { flex-shrink: 0; margin-top: 1px; }
`;

const FieldRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 12px;

        > div {
            flex: 1;
        }
    }
`;

const FieldColumn = styled.div`
    text-align: left;
`;

const InputLabel = styled.span`
    display: block;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 6px;
`;

const OptionsArea = styled.div`
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px dashed ${colors.night}1A;
`;

const OptionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

const OptionNumber = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    min-width: 18px;
`;

const RemoveOptionBtn = styled.button`
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 8px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s ease;
    padding: 0;

    &:hover {
        background: ${colors.danger}0A;
    }
`;

const AddOptionBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: ${fonts.sizes.small};
    font-weight: 500;
    color: ${panelAccent(colors.secondaryPurple)};
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 0;
    transition: opacity 0.15s ease;

    &:hover { opacity: 0.7; }
`;

const SwitchRow = styled.div`
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px dashed ${colors.night}1A;
`;

const PreviewPane = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
    border-radius: 12px;
    background: ${colors.neutralLight};
    border: 1.5px dashed ${panelAccentAlpha(colors.occasionalPurple, '44')};

    @media screen and (min-width: ${breakpoints.lg}) {
        position: sticky;
        top: 0;
    }
`;

const PreviewEyebrow = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: fit-content;
    font-size: ${fonts.sizes.mini};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: ${panelAccent(colors.occasionalPurple)};
`;

const PreviewEmpty = styled.p`
    margin: 0;
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    line-height: 1.5;
`;

const PreviewMock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const PreviewMockLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const RequiredMark = styled.span`
    color: ${colors.danger};
    margin-left: 4px;
`;

const mockControlStyles = `
    width: 100%;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid ${colors.night}26;
    background: ${colors.neutralLight};
    font-size: ${fonts.sizes.default};
    color: ${colors.nightLight};
    pointer-events: none;
`;

const MockInput = styled.input`
    ${mockControlStyles}
`;

const MockTextarea = styled.textarea`
    ${mockControlStyles}
    resize: none;
    min-height: 64px;
    font-family: inherit;
`;

const MockUpload = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px dashed ${colors.night}33;
    background: ${colors.neutralLight};
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

const MockUploadHint = styled.span`
    margin-left: auto;
    font-size: ${fonts.sizes.mini};
    font-weight: 400;
    color: ${colors.nightLight};
`;

const MockOptionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MockOptionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${fonts.sizes.small};
    color: ${colors.nightDark};
`;

const MockOptionControl = styled.span`
    width: 15px;
    height: 15px;
    min-width: 15px;
    border: 1.5px solid ${colors.night}44;
    border-radius: ${({ round }) => round ? '50%' : '4px'};
    background: ${colors.neutralLight};
`;

const AddFieldArea = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 16px;
    margin-bottom: 24px;
`;

const AddFieldBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border-radius: 10px;
    border: 1px dashed ${panelAccentAlpha(colors.occasionalPurple, '66')};
    background: transparent;
    color: ${panelAccent(colors.occasionalPurple)};
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    width: 100%;
    justify-content: center;

    &:hover:not(:disabled) {
        background: ${panelAccentAlpha(colors.occasionalPurple, '08')};
        border-color: ${panelAccent(colors.occasionalPurple)};
    }

    &:disabled {
        opacity: 0.35;
        cursor: default;
    }
`;

const FooterButtons = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;

    @media screen and (max-width: ${breakpoints.sm}) {
        flex-direction: column;
    }
`;
